import { type Colors, extendTheme, StyleFunctionProps, ThemeOverride } from '@chakra-ui/react';
/* Deep import, not the package barrel: the barrel re-exports every component,
 * so importing tokens from it drags React and heroicons into anything that
 * touches this theme, including backend bundles that only need the colours. */
import { colorScales } from '@main/complykit/tokens/colors';

import { StatusTag } from '../status-tag/theme';
import { Table } from '../table/theme';
import { checkboxTheme } from './checkbox';
import { switchTheme } from './switch';
import { TagTheme } from './tag';

/** ComplyKit's neutral, the single source for the greys below. Indexing a
 * scale widens to `| undefined`, so the step lookup asserts once here rather
 * than at each of the eleven call sites. */
const step = (scale: Record<number, string>, n: number): string => scale[n] as string;
const ckNeutral = {
  light: (n: number) => step(colorScales.neutral.regular, n),
  dark: (n: number) => step(colorScales.neutral.dark, n),
};

const colors: Colors = {
  blue: {
    '25': '#F6F4FE',
    '50': '#ede9fd',
    '100': '#dbd3fb',
    '200': '#b7a6f7',
    '300': '#937af4',
    '400': '#6f4df0',
    '500': '#4b21ec',
    '600': '#3c1abd',
    '700': '#2d148e',
    '800': '#1e0d5e',
    '900': '#0f072f',
  },
  /* The neutral ramp resolves to ComplyKit's, so every `gray.N` in the product
   * converges on one palette without touching a call site. Chakra's default
   * grey is blue-tinted (the most-used step was 37 points of blue over red),
   * which read as a cold cast beside ComplyKit's pure neutral surfaces.
   *
   * Chakra models grey as a single light-to-dark ramp while ComplyKit, following
   * Radix, ships separate light and dark scales. The low steps are used as
   * light-mode surfaces and the high ones almost always sit in the dark slot of
   * useColorModeValue, so each half maps to the scale that actually serves it.
   * That is why 700-900 descend into the dark values rather than continuing the
   * light ramp.
   *
   * This is transitional: it exists so unmigrated Chakra components share the
   * ComplyKit palette during the migration. Components should move to ComplyKit
   * surface tokens rather than gaining new `gray.N` references. */
  gray: {
    '25': ckNeutral.light(1),
    '50': ckNeutral.light(2),
    '100': ckNeutral.light(3),
    '200': ckNeutral.light(5),
    '300': ckNeutral.light(7),
    '400': ckNeutral.light(9),
    '500': ckNeutral.light(10),
    '600': ckNeutral.light(11),
    '700': ckNeutral.dark(4),
    '800': ckNeutral.dark(2),
    '900': ckNeutral.dark(1),
  },
  purple: {
    '100': '#EDE9FD',
    '800': '#6F4DF0',
  },
};

const config: ThemeOverride = {
  components: {
    Button: {
      variants: {
        outline: (props: StyleFunctionProps) => ({
          _active: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.50',
          },
        }),
      },
    },
    Checkbox: checkboxTheme,
    StatusTag,
    Table,
    Tag: TagTheme,
    Input: {
      defaultProps: {
        errorBorderColor: 'red.300',
      },
    },
    Textarea: {
      defaultProps: {
        errorBorderColor: 'red.300',
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: 'xs',
        fontWeight: 'semibold',
      },
      variants: {
        default: {
          fontSize: 'md',
          fontWeight: 'medium',
        },
      },
    },
    Switch: switchTheme,
    /* Chakra hardcodes a white panel for each of these (--modal-bg and
     * friends) rather than reading a semantic token, so each is pointed at
     * the overlay surface explicitly. They float above the page, so overlay
     * is the correct role in both modes. */
    Modal: {
      baseStyle: {
        dialog: {
          '--modal-bg': 'colors.surface-overlay',
          '--ck-surface': 'var(--ck-surface-overlay)',
        },
      },
    },
    Drawer: {
      baseStyle: {
        dialog: {
          '--drawer-bg': 'colors.surface-overlay',
          '--ck-surface': 'var(--ck-surface-overlay)',
        },
      },
    },
    Popover: {
      baseStyle: {
        content: {
          '--popper-bg': 'colors.surface-overlay',
          '--ck-surface': 'var(--ck-surface-overlay)',
        },
      },
    },
    Menu: {
      baseStyle: {
        list: { '--menu-bg': 'colors.surface-overlay' },
      },
    },
    Divider: {
      defaultProps: {
        size: 'sm',
        borderColor: 'border-subtle',
      },
      sizes: {
        lg: { borderWidth: '4px' },
        md: { borderWidth: '2px' },
        sm: { borderWidth: '1px' },
      },
    },
  },
  colors,
  /* The bridge to ComplyKit (docs/specs/frontend/design-system/tokens.md).
   *
   * Chakra resolves its own surfaces through these semantic tokens, so
   * pointing them at ComplyKit's custom properties converts every inherited
   * component at once: modals, drawers, popovers, menus, tables, dividers.
   * No component changes, and the values still swap with the theme because
   * ComplyKit publishes them per mode.
   *
   * Both branches name the same variable deliberately. ComplyKit already
   * resolves it per mode, so splitting _light and _dark here would create a
   * second place where mode logic lives, and the two would drift. */
  semanticTokens: {
    colors: {
      'chakra-body-bg': {
        _light: 'var(--ck-surface-base)',
        _dark: 'var(--ck-surface-base)',
      },
      'chakra-subtle-bg': {
        _light: 'var(--ck-surface-recessed)',
        _dark: 'var(--ck-surface-recessed)',
      },
      'chakra-border-color': {
        _light: 'var(--ck-border-subtle)',
        _dark: 'var(--ck-border-subtle)',
      },
      'chakra-body-text': {
        _light: 'var(--ck-text-default)',
        _dark: 'var(--ck-text-default)',
      },
      'chakra-subtle-text': {
        _light: 'var(--ck-text-muted)',
        _dark: 'var(--ck-text-muted)',
      },
      /* Surfaces that sit ON the page rather than being it: modal and drawer
       * panels, popovers, menus. Chakra hardcodes these per component, so
       * they are named here and referenced from the component themes. */
      'surface-raised': {
        _light: 'var(--ck-surface-raised)',
        _dark: 'var(--ck-surface-raised)',
      },
      'surface-overlay': {
        _light: 'var(--ck-surface-overlay)',
        _dark: 'var(--ck-surface-overlay)',
      },
      'border-subtle': {
        _light: 'var(--ck-border-subtle)',
        _dark: 'var(--ck-border-subtle)',
      },
    },
  },
  fonts: {
    heading: `Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
    body: `Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
  },
  initialColorMode: 'dark',
  useSystemColorMode: true,
  styles: {
    global: {
      // default width and height for svg icons to 1em, to fix issue in safari
      svg: {
        minWidth: '1em',
        minHeight: '1em',
      },
    },
  },
};

export const theme = extendTheme(config);
